import { Injectable, Injector, DOCUMENT, inject } from '@angular/core';
import {
  CoreActionCheckAppMinVersion,
  CoreActionLogin,
  CoreActionResetLoginSuccess,
  CoreActions,
  CoreActionSaveLoginCheck,
  CoreActionSetBiometricPassword,
  CoreActionsTypes,
  CoreActionUpdateLogin,
  CoreActionUpdateUserLanguage,
  CoreBackendIDs,
  CoreHttpActions,
  CoreHttpActionSetError,
  CoreHttpActionSetHttpResponseMessage,
  CoreHttpActionSetMaintenanceMode,
  CoreHttpError,
  CoreLoginOptionsType,
  CoreRouterActionNavigateForward,
  CoreRouterActionNavigateRoot,
  CoreRouterActions,
  CoreUtilsService,
  BiometricService,
  CORE_BACKEND,
  selectorCoreAppVersion,
  selectorCoreFCMToken,
  selectorCoreHttpError,
  selectorCoreInstanceInfo,
  resolvePickerLocale,
  selectorCoreLogin
} from '@bepatient/core';
import { Device } from '@capacitor/device';
import { AlertController, Platform, ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { OnboardingApiService } from './onboarding-api.service';
import {
  OnboardingActionAccountExists,
  OnboardingActionChangePassword,
  OnboardingActionChangePasswordFailed,
  OnboardingActionChangePasswordSuccess,
  OnboardingActionFinalizeOnboarding,
  OnboardingActionFinalizeOnboardingFailed,
  OnboardingActionFinalizeOnboardingSuccess,
  OnboardingActionLoadOnboardingFailed,
  OnboardingActionLoadOnboardingSuccess,
  OnboardingActionLoadTosItemDetails,
  OnboardingActionLoadTosItemDetailsFailed,
  OnboardingActionLoadTosItemDetailsSuccess,
  OnboardingActionPasswordReset,
  OnboardingActionPasswordResetFailed,
  OnboardingActionPasswordResetForId,
  OnboardingActionPasswordResetForIdFailed,
  OnboardingActionPasswordResetSuccess,
  OnboardingActions,
  OnboardingActionSendStep,
  OnboardingActionSendStepFailed,
  OnboardingActionSendStepSuccess,
  OnboardingActionSendStepV2,
  OnboardingActionSendStepV2Failed,
  OnboardingActionSendStepV2Success,
  OnboardingActionSetBiometricLogin,
  OnboardingActionLoadBiometricFailed,
  OnboardingActionSetBirthdate,
  OnboardingActionSetBirthdateButton,
  OnboardingActionSetBirthdateFormat,
  OnboardingActionSetBirthdateLabel,
  OnboardingActionSetEmail,
  OnboardingActionSetPassword,
  OnboardingActionTypes,
  OnboardingActionSetBirthdateLanguage,
  OnboardingActionSetSwiperNavigateTo
} from './onboarding.actions';
import { OnboardingStepTypes } from './onboarding.models';
import {
  selectorOnboardingEmail,
  selectorOnboardingPasswordResetIAuth
} from './onboarding.reducer';

@Injectable()
export class OnboardingEffects {
  private injector = inject(Injector);
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private translate = inject(TranslateService);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private onboardingApiService = inject(OnboardingApiService);
  private platform = inject(Platform);
  private coreUtilsService = inject(CoreUtilsService);
  private biometric = inject(BiometricService);
  private document = inject(DOCUMENT);

  setEmail$: Observable<
    CoreActions | CoreRouterActions | OnboardingActions | CoreHttpActions
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SET_EMAIL),
      map((action: OnboardingActionSetEmail) => action.payload),
      switchMap(email =>
        this.onboardingApiService.authorize(email).pipe(
          map(
            res =>
              new CoreActionUpdateLogin({
                ...res,
                loginId: this.coreUtilsService.textToBase64(email),
                userMustEnterOnboarding: true
              })
          ),
          catchError(({ error, status }) => {
            // SMARTAPP: Temporary fix
            if (
              error !== undefined &&
              error.message !== undefined &&
              error.message.format !== undefined
            ) {
              error = error.message;
              const language = error.user_language;
              this.store$.dispatch(
                new CoreActionUpdateUserLanguage({
                  language,
                  rightToLeft: language ? language === 'ar' : false
                })
              );
            }
            switch (status) {
              // APP-2190: To protect data, changing default behaviour for all the existing user
              // For newly registered user:
              // Go through the onboarding process
              case 400:
                const birthdateFormat =
                  this.onboardingApiService.getBirthdateFormat(error.format);
                switch (this.injector.get(CORE_BACKEND)) {
                  case CoreBackendIDs.SMART:
                    const birthdateLabel = error.field_label;
                    const birthdateButton = error.button_label;
                    const birthdateLanguage = error.user_language;
                    localStorage.setItem(
                      'locale',
                      resolvePickerLocale(birthdateLanguage)
                    );
                    return error &&
                      error.missings &&
                      error.missings.includes('birthdate')
                      ? [
                          new OnboardingActionSetBirthdateFormat(
                            birthdateFormat
                          ),
                          new OnboardingActionSetBirthdateLabel(birthdateLabel),
                          new OnboardingActionSetBirthdateButton(
                            birthdateButton
                          ),
                          new OnboardingActionSetBirthdateLanguage(
                            birthdateLanguage
                          ),
                          // new CoreRouterActionNavigateForward({
                          //   url: 'onboarding-birthdate'
                          // }),
                          new OnboardingActionSetSwiperNavigateTo(
                            CoreLoginOptionsType.LOGIN_ONBOARDING_BIRTHDAY
                          ),
                          new CoreActionCheckAppMinVersion()
                        ]
                      : [
                          new CoreActionResetLoginSuccess(),
                          // new CoreRouterActionNavigateForward({
                          //   url: 'onboarding-password'
                          // }),
                          new OnboardingActionSetSwiperNavigateTo(
                            CoreLoginOptionsType.LOGIN_PASSWORD
                          ),
                          new CoreActionCheckAppMinVersion()
                        ];

                  default:
                    return error &&
                      error.missings &&
                      error.missings.includes('birthdate')
                      ? [
                          new OnboardingActionSetBirthdateFormat(
                            birthdateFormat
                          ),
                          // new CoreRouterActionNavigateForward({
                          //   url: 'onboarding-birthdate'
                          // }),
                          new OnboardingActionSetSwiperNavigateTo(
                            CoreLoginOptionsType.LOGIN_ONBOARDING_BIRTHDAY
                          ),
                          new CoreActionCheckAppMinVersion()
                        ]
                      : [
                          new CoreActionResetLoginSuccess(),
                          new OnboardingActionSetSwiperNavigateTo(
                            CoreLoginOptionsType.LOGIN_PASSWORD
                          ),
                          // new CoreRouterActionNavigateForward({
                          //   url: 'onboarding-password'
                          // }),
                          new CoreActionCheckAppMinVersion()
                        ];
                }
              // For maintenance mode:
              case 503:
                const coreHttpError: CoreHttpError = {
                  message: void 0,
                  name: '',
                  status: void 0,
                  statusText: ''
                };
                return [
                  new CoreHttpActionSetError(coreHttpError),
                  new CoreHttpActionSetMaintenanceMode()
                ];
              // APP-2190: Unless user enter newly registered email or id,
              // it goes to password page either login id correct or incorrect
              default:
                return [
                  new CoreActionResetLoginSuccess(),
                  new OnboardingActionSetSwiperNavigateTo(
                    CoreLoginOptionsType.LOGIN_PASSWORD
                  ),
                  // new CoreRouterActionNavigateForward({
                  //   url: 'onboarding-password'
                  // }),
                  new CoreActionCheckAppMinVersion()
                ];
            }
          })
        )
      )
    )
  );

  setBirthdate$: Observable<CoreActions | OnboardingActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.SET_BIRTHDATE),
        map((action: OnboardingActionSetBirthdate) => action.payload),
        withLatestFrom(
          this.store$.pipe(select(selectorOnboardingEmail)),
          this.store$.pipe(select(selectorCoreFCMToken)),
          this.store$.pipe(select(selectorCoreAppVersion)),
          from(Device.getInfo()),
          (birthdate, email, fcmToken, appVersion, device: any) => [
            birthdate,
            email,
            fcmToken,
            appVersion,
            device
          ]
        ),
        switchMap(([birthdate, email, fcmToken, appVersion, device]) => {
          const deviceType = device.platform || void 0;
          const userTimezone = moment.tz.guess();
          return this.onboardingApiService
            .authorize(
              email,
              birthdate,
              fcmToken,
              deviceType,
              userTimezone,
              appVersion
            )
            .pipe(
              map(
                res =>
                  new CoreActionUpdateLogin({
                    ...res,
                    loginId: this.coreUtilsService.textToBase64(email),
                    userMustEnterOnboarding: true
                  })
              ),
              catchError(() => of(new OnboardingActionAccountExists(false)))
            );
        })
      )
  );

  setPassword$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SET_PASSWORD),
      map((action: OnboardingActionSetPassword) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorOnboardingEmail)),
        this.store$.pipe(select(selectorCoreFCMToken)),
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo()),
        (password, email, fcmToken, appVersion, device: any) => [
          password,
          email,
          fcmToken,
          appVersion,
          device
        ]
      ),
      map(([password, email, fcmToken, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        const userTimezone = moment.tz.guess();
        return new CoreActionLogin({
          login: email,
          password: password,
          fcmToken: fcmToken,
          deviceType: deviceType,
          userTimezone: userTimezone,
          appVersion: appVersion
        });
      })
    )
  );

  loadBiometric$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.LOAD_BIOMETRIC),
        tap(async () => {
          // Block biometric login on rooted/jailbroken devices.
          if (await this.biometric.isDeviceRooted()) {
            await this.biometric.disableBiometric('rooted');
            this.store$.dispatch(
              new OnboardingActionLoadBiometricFailed(
                this.biometric.deviceCompromisedError()
              )
            );
            return;
          }
          this.biometric
            .showBiometricPrompt(true)
            .then(() => {
              this.biometric
                .loadBiometricSecret()
                .then(secret => {
                  // Get biometric data from local Storage
                  let biometricData = this.biometric.getDeviceBiometricData();
                  if (
                    biometricData &&
                    (biometricData.enableBiometricLogin == 'false' ||
                      biometricData.enableBiometricLogin == 'disabled')
                  ) {
                    biometricData = {
                      ...biometricData,
                      enableBiometricLogin: 'true',
                      permission: 'true'
                    };
                    this.biometric.setDeviceBiometricData(biometricData);
                  }

                  let userData = secret;
                  if (userData['username'] && userData['password']) {
                    const uID = userData['username'];
                    const uPass = userData['password'];
                    this.store$.dispatch(
                      new OnboardingActionSetBiometricLogin({ uID, uPass })
                    );
                  }
                })
                .catch(error => {
                  this.store$.dispatch(
                    new OnboardingActionLoadBiometricFailed(
                      this.biometric.parseBiometricError(error)
                    )
                  );
                });
            })
            .catch(error => {
              this.store$.dispatch(
                new OnboardingActionLoadBiometricFailed(
                  this.biometric.parseBiometricError(error)
                )
              );
            });
        })
      ),
    { dispatch: false }
  );

  loadBiometricFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.LOAD_BIOMETRIC_FAILED),
        map((action: OnboardingActionLoadBiometricFailed) => action.payload),
        tap(error => {
          // Get biometric data from local Storage
          let biometricData = this.biometric.getDeviceBiometricData();
          this.biometric
            .isBiometricAvailable()
            .then(() => {
              this.coreUtilsService.presentToaster(error.message);
              if (error && (!error.code || error.code === '1')) {
                biometricData = {
                  ...biometricData,
                  enableBiometricLogin: 'disabled'
                };
                this.biometric.setDeviceBiometricData(biometricData);
              }
            })
            .catch(() => {
              this.coreUtilsService.presentToaster(error.message);
              if (biometricData && biometricData.enableBiometricLogin) {
                biometricData.enableBiometricLogin = 'disabled';
                this.biometric.setDeviceBiometricData(biometricData);
              }
            });
        })
      ),
    { dispatch: false }
  );

  setBiometricLogin$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SET_BIOMETRIC_LOGIN),
      map((action: OnboardingActionSetBiometricLogin) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreFCMToken)),
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo()),
        ({ uID, uPass }, fcmToken, appVersion, device) => [
          uID,
          uPass,
          fcmToken,
          appVersion,
          device
        ]
      ),
      map(([email, password, fcmToken, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        const userTimezone = moment.tz.guess();
        return new CoreActionLogin({
          login: email,
          password: password,
          fcmToken: fcmToken,
          deviceType: deviceType,
          userTimezone: userTimezone,
          loginType: 'biometric',
          appVersion: appVersion
        });
      })
    )
  );

  passwordReset$: Observable<
    OnboardingActions | CoreActions | CoreHttpActions
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.PASSWORD_RESET),
      map((action: OnboardingActionPasswordReset) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo()),
        (password, appVersion, device: any) => [password, appVersion, device]
      ),
      switchMap(([password, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        return this.onboardingApiService
          .passwordReset(password, deviceType, appVersion)
          .pipe(
            mergeMap(res => {
              return [
                new OnboardingActionPasswordResetSuccess(res.iAuth),
                new CoreHttpActionSetHttpResponseMessage(res.message),
                new CoreActionSetBiometricPassword({
                  password: password?.newPassword
                })
              ];
            }),
            catchError(() => of(new OnboardingActionPasswordResetFailed()))
          );
      })
    )
  );

  passwordResetSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.PASSWORD_RESET_SUCCESS),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'onboarding-password-changed'
          })
      )
    )
  );

  passwordResetFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.PASSWORD_RESET_FAILED),
        withLatestFrom(this.store$.select(selectorCoreHttpError)),
        tap(([, error]) =>
          this.alertController
            .create({
              header: (error && error.statusText) || 'Error',
              message: error && error.message,
              cssClass: 'bepatient_dynamic_global_alert',
              mode: 'ios',
              buttons: [
                {
                  text: this.translate.instant('OK'),
                  cssClass: ['default']
                }
              ]
            })
            .then(alert => alert.present())
        )
      ),
    { dispatch: false }
  );

  passwordResetCompleted$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.PASSWORD_RESET_COMPLETED),
      withLatestFrom(this.store$.select(selectorOnboardingPasswordResetIAuth)),
      map(
        ([, iAuth]) =>
          new CoreActionUpdateLogin({ iAuth, userMustChangePassword: void 0 })
      )
    )
  );

  changePassword$: Observable<
    OnboardingActions | CoreRouterActions | CoreActions
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.CHANGE_PASSWORD),
      map((action: OnboardingActionChangePassword) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo())
      ),
      switchMap(([email, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        return this.onboardingApiService
          .changePassword(email, void 0, deviceType, appVersion)
          .pipe(
            map(() => new OnboardingActionChangePasswordSuccess()),
            catchError(({ error }) => {
              if (error) {
                error = error.message;
                const language = error.user_language;
                const rightToLeft = language ? language === 'ar' : false;
                this.store$.dispatch(
                  new CoreActionUpdateUserLanguage({
                    language,
                    rightToLeft: rightToLeft
                  })
                );
                localStorage.setItem('locale', resolvePickerLocale(language));
              }
              return error &&
                error.missings &&
                error.missings.includes('birthdate')
                ? [
                    new OnboardingActionSetBirthdateFormat(
                      this.onboardingApiService.getBirthdateFormat(error.format)
                    ),
                    new OnboardingActionSetSwiperNavigateTo(
                      CoreLoginOptionsType.LOGIN_RESET_BIRTHDAY
                    )
                    // new CoreRouterActionNavigateForward({
                    //   url: 'onboarding-password-reset-birthdate'
                    // })
                  ]
                : of(new OnboardingActionChangePasswordFailed());
            })
          );
      })
    )
  );

  confirmBirthDateToChangePassword$: Observable<
    OnboardingActions | CoreActions
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.PASSWORD_RESET_FOR_ID),
      map((action: OnboardingActionPasswordResetForId) => {
        return action.payload;
      }),
      withLatestFrom(
        this.store$.pipe(select(selectorOnboardingEmail)),
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo())
      ),
      switchMap(([birthdate, email, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        return this.onboardingApiService
          .changePassword(email, birthdate, deviceType, appVersion)
          .pipe(
            map(
              data =>
                new CoreActionUpdateLogin({
                  iAuth: data.iauth,
                  loginId: this.coreUtilsService.textToBase64(email),
                  passwordResetInterface: data.password_reset_interface,
                  tokenValidationCheckFrequency:
                    data.token_validation_check_frequency,
                  tokenWarningExpirationDuration:
                    data.token_warning_expiration_duration,
                  language: data.user_language,
                  userMustChangePassword: true
                })
            ),
            catchError(() => of(new OnboardingActionPasswordResetForIdFailed()))
          );
      })
    )
  );

  changePasswordSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.CHANGE_PASSWORD_SUCCESS),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'onboarding-password-forgotten'
          })
      )
    )
  );

  changePasswordFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.CHANGE_PASSWORD_FAILED),
        withLatestFrom(this.store$.select(selectorCoreHttpError)),
        tap(([, error]) =>
          this.alertController
            .create({
              header: (error && error.statusText) || 'Error',
              message: error && error.message,
              cssClass: 'bepatient_dynamic_global_alert',
              mode: 'ios',
              buttons: [
                {
                  text: this.translate.instant('OK')
                }
              ]
            })
            .then(alert => alert.present())
        )
      ),
    { dispatch: false }
  );

  updateUserLanguageOnAuthorize$: Observable<CoreActionUpdateUserLanguage> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.UPDATE_LOGIN),
        withLatestFrom(this.store$.select(selectorCoreLogin)),
        map(([action, login]) => {
          const payload: CoreActionUpdateLogin['payload'] = (
            action as CoreActionUpdateLogin
          )?.payload;
          if (payload && payload.language && payload.language !== '') {
            return new CoreActionUpdateUserLanguage({
              language: payload.language,
              rightToLeft: payload.rightToLeft ?? payload.language === 'ar'
            });
          }
          const fromStore = login?.language;
          const language =
            fromStore != null && fromStore !== '' ? fromStore : 'en';
          const rightToLeft =
            fromStore != null && fromStore !== '' && login
              ? login.rightToLeft
              : language === 'ar';
          return new CoreActionUpdateUserLanguage({ language, rightToLeft });
        })
      )
    );

  loadOnboardingTosDetails$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.LOAD_ONBOARDING_TOS_ITEM_DETAILS),
      map((action: OnboardingActionLoadTosItemDetails) => action.payload),
      switchMap(url =>
        this.onboardingApiService.loadOnboardingTosDetails(url).pipe(
          map(data => {
            return new OnboardingActionLoadTosItemDetailsSuccess(data);
          }),
          catchError(() => of(new OnboardingActionLoadTosItemDetailsFailed()))
        )
      )
    )
  );

  loadOnboarding$: Observable<OnboardingActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.UPDATE_LOGIN),
      map((action: CoreActionUpdateLogin) => action.payload),
      filter(payload => payload.userMustEnterOnboarding),
      switchMap(() =>
        this.onboardingApiService.loadOnboarding().pipe(
          withLatestFrom(this.store$.select(selectorCoreInstanceInfo)),
          map(([onboarding, instance]) => {
            onboarding.steps.forEach(step => {
              if (!step.color) {
                step.color = instance.appColor;
              }
              if (step.type === OnboardingStepTypes.INTRO) {
                step.instanceLogo = instance.logo;
              }
              if (step.type === OnboardingStepTypes.CONCLUSION) {
                step.instanceLogo = instance.logo;
              }
            });
            return new OnboardingActionLoadOnboardingSuccess(onboarding);
          }),
          catchError(() => of(new OnboardingActionLoadOnboardingFailed()))
        )
      )
    )
  );

  loadOnboardingSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.LOAD_ONBOARDING_SUCCESS),
      map(
        () =>
          new CoreRouterActionNavigateRoot({
            url: 'onboarding-steps',
            options: { animationDirection: 'forward' }
          })
      )
    )
  );

  loadOnboardingFailed$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.LOAD_ONBOARDING_FAILED),
      withLatestFrom(this.store$.select(selectorCoreHttpError)),
      map(() => {
        this.store$.dispatch(new CoreActionResetLoginSuccess());
        return new CoreRouterActionNavigateForward({
          url: 'onboarding-password'
        });
      })
    )
  );

  setSelectedTosIndex$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SET_SELECTED_TOS_INDEX),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'onboarding-details-tos'
          })
      )
    )
  );

  sendStep$: Observable<OnboardingActions | CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SEND_STEP),
      map((action: OnboardingActionSendStep) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo())
      ),
      switchMap(([stepsManager, appVersion, device]) => {
        const deviceType = device.platform || void 0;
        return this.onboardingApiService
          .onboardingSubmitStep(
            stepsManager.currentStepValuesForMobi,
            deviceType,
            appVersion
          )
          .pipe(
            switchMap(res =>
              res.iAuth
                ? [
                    new CoreActionUpdateLogin({
                      ...res,
                      userIsInOnboarding: true,
                      userMustEnterOnboarding: void 0
                    }),
                    new OnboardingActionSendStepSuccess(stepsManager)
                  ]
                : [new OnboardingActionSendStepSuccess(stepsManager)]
            ),
            catchError(() => of(new OnboardingActionSendStepFailed()))
          );
      })
    )
  );

  sendStepV2$: Observable<OnboardingActions | CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.SEND_STEP_V2),
      map((action: OnboardingActionSendStepV2) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreFCMToken)),
        this.store$.pipe(select(selectorCoreAppVersion)),
        from(Device.getInfo())
      ),
      switchMap(([stepsManagerV2, fcmToken, appVersion, device]) => {
        /**
         *  Create Object to send Current Onboarding Step Value
         *  with the fcm token
         */
        const deviceType = device.platform || void 0;
        const data = {
          ...stepsManagerV2.currentStepValues,
          fcm_token: fcmToken || void 0
          // fcm_token: 'aHWZK11YpUXEA32Kxd713f9jXGUpKdqJFRIgL4Gk8TwBmDJxtI' // Fake FCM Token for testing in browser
        };
        return this.onboardingApiService
          .onboardingSubmitStep(data, deviceType, appVersion)
          .pipe(
            switchMap(res =>
              res.iAuth
                ? [
                    new CoreActionUpdateLogin({
                      ...res,
                      userIsInOnboarding: true,
                      userMustEnterOnboarding: void 0
                    }),
                    new OnboardingActionSendStepV2Success(stepsManagerV2),
                    new CoreActionSaveLoginCheck()
                  ]
                : [new OnboardingActionSendStepV2Success(stepsManagerV2)]
            ),
            catchError(() => of(new OnboardingActionSendStepV2Failed()))
          );
      })
    )
  );

  sendStepSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.SEND_STEP_SUCCESS),
        map((action: OnboardingActionSendStepSuccess) => action.payload),
        tap(stepsManager => stepsManager.stepSubmissionSuccess())
      ),
    { dispatch: false }
  );

  sendStepV2Success$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.SEND_STEP_V2_SUCCESS),
        map((action: OnboardingActionSendStepV2Success) => action.payload),
        tap(stepsManagerV2 => stepsManagerV2.stepSubmissionSuccess())
      ),
    { dispatch: false }
  );

  finalizeOnboarding$: Observable<OnboardingActions | CoreActions> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.FINALIZE_ONBOARDING),
        map((action: OnboardingActionFinalizeOnboarding) => action.payload),
        switchMap(stepsManager =>
          this.onboardingApiService.onboardingFinalize().pipe(
            switchMap(res =>
              res.iAuth
                ? [
                    new CoreActionUpdateLogin(res),
                    new OnboardingActionFinalizeOnboardingSuccess(stepsManager)
                  ]
                : [new OnboardingActionFinalizeOnboardingSuccess(stepsManager)]
            ),
            catchError(() => of(new OnboardingActionFinalizeOnboardingFailed()))
          )
        )
      )
    );

  finalizeOnboardingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.FINALIZE_ONBOARDING_SUCCESS),
        map(
          (action: OnboardingActionFinalizeOnboardingSuccess) => action.payload
        ),
        tap(stepsManager => stepsManager.finalizeSubmissionSuccess())
      ),
    { dispatch: false }
  );

  onboardingCompleted$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActionTypes.ONBOARDING_COMPLETED),
      switchMap(() => [
        new CoreActionUpdateLogin({
          userIsInOnboarding: void 0,
          userMustEnterOnboarding: void 0
        })
      ])
    )
  );

  sendStepFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActionTypes.SEND_STEP_V2_FAILED),
        withLatestFrom(this.store$.select(selectorCoreHttpError)),
        tap(([, error]) =>
          this.alertController
            .create({
              header: this.translate.instant('ERROR'),
              message: error && error.message,
              cssClass: 'bepatient_dynamic_global_alert',
              mode: 'ios',
              buttons: [
                {
                  text: this.translate.instant('OK')
                }
              ]
            })
            .then(alert => alert.present())
        )
      ),
    { dispatch: false }
  );
}
